import PropTypes from 'prop-types';
import React, { memo, useCallback, useEffect } from 'react';
import { connect } from 'react-redux';
import Helmet from 'react-helmet';
import { bindActionCreators } from 'redux';

import connectOptions, { mergeProps } from '../utils/connectOptions';
import { MailingHistory } from '../components/MailingHistory';
import { buildSortByParam } from '../helpers/paramBuilder';
import { getMailingHistory, updateMailingHistory } from '../actions/mailingHistory';
import { showError } from '../utils/MessagePopup';
import config from '../config';
import { isResearcher } from '../utils/checkPermissions';
import { usePopupContext } from '../contexts/PopupContext';

const { func, object } = PropTypes;

/** @class
 *  Mailing history page container.
 *  Change title to `"Mailing History Page"`.
 */
const MailingHistoryContainer = ({
  user,
  mailingHistory,
  history,
  getMailingHistory,
  updateMailingHistory,
}) => {
  const { openPopup, closePopup } = usePopupContext();

  const hasAccess = () => {
    if (!user) return false;

    return !isResearcher(user.getIn(['roles', 0, 'slug'], null));
  };

  useEffect(() => {
    if (!hasAccess()) {
      showError(openPopup, config.permisionError, () => {
        history.push('/');
        closePopup();
      });

      return;
    }

    getMailingHistory({ userId: user.get('id') });
  }, []);

  useEffect(() => {
    const sortBy = buildSortByParam(mailingHistory);

    getMailingHistory({
      userId: user.get('id'),
      page: 1,
      sortBy,
    });
  }, [mailingHistory.get('sortBy')]);

  const handleGetNextPageData = useCallback(
    page => {
      const sortBy = buildSortByParam(mailingHistory);

      getMailingHistory({
        userId: user.get('id'),
        page,
        sortBy,
      });
    },
    [mailingHistory, user, getMailingHistory],
  );

  const handleChange = useCallback(
    data => {
      updateMailingHistory({
        filterName: data.filterName,
        filterData: data.filterData,
      });
    },
    [updateMailingHistory],
  );

  const handleDoubleClickRow = useCallback(row => {
    window.open(`/mailing-info?id=${row.data.recordId}`, '_blank');
  }, []);

  const handleReload = useCallback(() => {
    getMailingHistory({
      userId: user.get('id'),
      page: 1,
    });
  }, [user, getMailingHistory]);

  if (!hasAccess()) return null;

  return (
    <div className="full-height">
      <Helmet title="Mailing History Page" />
      <MailingHistory
        mailingHistory={mailingHistory}
        onChange={handleChange}
        onDoubleClickRow={handleDoubleClickRow}
        onGetNextPageData={handleGetNextPageData}
        onReload={handleReload}
        user={user}
      />
    </div>
  );
};

MailingHistoryContainer.propTypes = {
  getMailingHistory: func.isRequired,
  mailingHistory: object.isRequired,
};

export { MailingHistoryContainer };
export default connect(
  state => ({
    mailingHistory: state.mailingHistory,
    user: state.auth.get('user', null),
  }),
  dispatch =>
    bindActionCreators(
      {
        getMailingHistory,
        updateMailingHistory,
      },
      dispatch,
    ),
  mergeProps,
  connectOptions,
)(memo(MailingHistoryContainer));
